import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { teacherLeaveService } from '../../../services/teacherLeaveService';
import { authService } from '../../../services/authService';
import { toast } from 'react-toastify';
import { FileText, Clock, CheckCircle, XCircle, Plus } from 'lucide-react';
import {
  TeacherPageShell,
  TeacherPageHeader,
  TeacherHeaderActions,
  TeacherStatsGrid,
  TeacherStatCard,
  TeacherFilterBar,
  TeacherSelect,
  TeacherButton,
  TeacherPanel,
  TeacherLoading,
  TeacherEmpty,
} from '../shared';
import StudentLeavesTab from './StudentLeavesTab';

type TabKey = 'teacher' | 'student';

const TeacherLeave: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState<TabKey>('teacher');
  const [leaves, setLeaves] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [editing, setEditing] = useState<any | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');

  const [form, setForm] = useState({
    reason: '',
    fromDate: '',
    toDate: '',
    leaveType: 'casual'
  });

  const leaveTypes = [
    { value: 'casual', label: 'Casual Leave' },
    { value: 'sick', label: 'Sick Leave' },
    { value: 'earned', label: 'Earned Leave' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        if (!user?.schoolId) return;
        const teacherId = authService.getTeacherId();
        if (!teacherId) return;
        const resp = await teacherLeaveService.getTeacherLeavesByTeacher(user.schoolId, teacherId);
        setLeaves(resp.leaves || []);
      } finally {
        setLoading(false);
      }
    };
    if (tab === 'teacher' && user?.schoolId) fetch();
  }, [tab, user?.schoolId]);

  const stats = useMemo(() => {
    const total = leaves.length;
    const pending = leaves.filter(l => (l.status || '').toLowerCase() === 'pending').length;
    const approved = leaves.filter(l => (l.status || '').toLowerCase() === 'approved').length;
    const rejected = leaves.filter(l => (l.status || '').toLowerCase() === 'rejected').length;
    return { total, pending, approved, rejected };
  }, [leaves]);

  const filteredLeaves = useMemo(() => {
    if (!filterStatus) return leaves;
    return leaves.filter(l => (l.status || '').toLowerCase() === filterStatus);
  }, [leaves, filterStatus]);

  const openApply = () => {
    setEditing(null);
    setForm({ reason: '', fromDate: '', toDate: '', leaveType: 'casual' });
    setFile(null);
    setOpen(true);
  };

  const openEdit = (leave: any) => {
    setEditing(leave);
    setForm({
      reason: leave.reason || '',
      fromDate: leave.startDate ? new Date(leave.startDate).toISOString().slice(0, 10) : '',
      toDate: leave.endDate ? new Date(leave.endDate).toISOString().slice(0, 10) : '',
      leaveType: leave.leaveType || 'casual'
    });
    setFile(null);
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!user?.schoolId) return;
    const teacherId = authService.getTeacherId();
    if (!teacherId) return;
    if (!form.reason || !form.fromDate || !form.toDate) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      if (editing?.leaveId) {
        await teacherLeaveService.updateTeacherLeave(user.schoolId, editing.leaveId, {
          startDate: form.fromDate,
          endDate: form.toDate,
          reason: form.reason,
          file: file || undefined,
        });
        toast.success('Leave updated successfully!');
      } else {
        await teacherLeaveService.createTeacherLeave({
          schoolId: user.schoolId,
          teacherId,
          startDate: form.fromDate,
          endDate: form.toDate,
          reason: form.reason,
          file: file || undefined,
        });
        toast.success('Leave applied successfully!');
      }
      // Refresh
      const resp = await teacherLeaveService.getTeacherLeavesByTeacher(user.schoolId, teacherId);
      setLeaves(resp.leaves || []);
      setOpen(false);
    } catch (error) {
      toast.error(editing ? 'Failed to update leave' : 'Failed to apply leave');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString || '-';
    }
  };

  const getStatusBadge = (status: string) => {
    const s = (status || 'pending').toLowerCase();
    if (s === 'approved') return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    if (s === 'rejected') return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
  };

  // Loading state
  if (loading && tab === 'teacher') {
    return <TeacherLoading message="Loading leave requests..." />;
  }

  return (
    <TeacherPageShell>
      <TeacherPageHeader
        title="Leave Management"
        description="Apply for leave and review student leave requests."
        action={
          tab === 'teacher' ? (
            <TeacherHeaderActions>
              <TeacherButton icon={Plus} compact onClick={openApply}>
                Apply Leave
              </TeacherButton>
            </TeacherHeaderActions>
          ) : undefined
        }
      />

      {/* Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setTab('teacher')}
          className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
            tab === 'teacher'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          My Leaves
        </button>
        <button
          onClick={() => setTab('student')}
          className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
            tab === 'student'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          Student Leaves
        </button>
      </div>

      {tab === 'teacher' && (
        <>
          <TeacherStatsGrid>
            <TeacherStatCard title="Total Leaves" value={stats.total} icon={FileText} color="indigo" />
            <TeacherStatCard title="Pending" value={stats.pending} icon={Clock} color="amber" />
            <TeacherStatCard title="Approved" value={stats.approved} icon={CheckCircle} color="emerald" />
            <TeacherStatCard title="Rejected" value={stats.rejected} icon={XCircle} color="rose" />
          </TeacherStatsGrid>

          <TeacherFilterBar>
            <TeacherSelect
              value={filterStatus}
              onChange={setFilterStatus}
              className="sm:w-48"
              options={[
                { value: '', label: 'All Status' },
                { value: 'pending', label: 'Pending' },
                { value: 'approved', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' },
              ]}
            />
          </TeacherFilterBar>

          {/* Leaves Table */}
          <TeacherPanel title="My Leave Requests" noPadding>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">From</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">To</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Attachment</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredLeaves.map(leave => (
                    <tr key={leave.leaveId} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{leave.reason || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{formatDate(leave.startDate || leave.fromDate)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{formatDate(leave.endDate || leave.toDate)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 capitalize">{leave.leaveType || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusBadge(leave.status)}`}>
                          {leave.status || 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {leave.filePath ? (
                          <a href={leave.filePath} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            View
                          </a>
                        ) : <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => openEdit(leave)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          title="Edit Leave"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredLeaves.length === 0 && (
              <TeacherEmpty
                icon={FileText}
                title="No leave requests yet"
                description='Click "Apply Leave" to submit your first leave request.'
              />
            )}
          </TeacherPanel>
        </>
      )}

      {tab === 'student' && <StudentLeavesTab />}

      {/* Apply / Edit Leave Dialog */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editing ? 'Edit Leave' : 'Apply Leave'}
              </h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason *</label>
                <textarea
                  rows={3}
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  placeholder="Enter reason for leave..."
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From Date *</label>
                  <input
                    type="date"
                    value={form.fromDate}
                    onChange={(e) => setForm({ ...form, fromDate: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To Date *</label>
                  <input
                    type="date"
                    value={form.toDate}
                    onChange={(e) => setForm({ ...form, toDate: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Leave Type</label>
                  <select
                    value={form.leaveType}
                    onChange={(e) => setForm({ ...form, leaveType: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {leaveTypes.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Attachment</label>
                  <label className="flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="text-sm truncate">{file ? file.name : 'Upload File'}</span>
                    <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                  </label>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white font-semibold shadow hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting && <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {editing ? 'Update' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </TeacherPageShell>
  );
};

export default TeacherLeave;
